import { useEffect, useState } from 'react';
import { tSyncConversationsContext } from './SyncConversations.types.ts';
import { tGetConversationsFromChatDaddyData } from '../IntegrationsProvider/Integrations.types.ts';
import { IntegrationsService } from '../IntegrationsProvider/Integrations.service.ts';
import { useWorkspaceContext } from '../WorkspaceProvider/Workspace.context.ts';
import { useSocketsContext } from '../SocketsProvider/Sockets.context.ts';
import { useToastContext } from '../ToastProvider/Toast.hook.ts';
import { eAlertType } from '../ToastProvider/Toast.enum.ts';
import { eIntegrationType } from '../IntegrationsProvider/Integrations.enums.ts';
import { AxiosError } from 'axios';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';

export function useSyncConversations(): tSyncConversationsContext {
  const [syncConversations, setSyncConversations] = useState<number>(0);
  const [totalConversations, setTotalConversations] = useState<number>(0);
  const [selectedIntegration, setSelectedIntegration] =
    useState<eIntegrationType>(eIntegrationType.WHATSAPP_WEB);
  const [selectedPagUUid, setSelectedPageUuid] = useState<string>('');
  const [conversations, setConversations] = useState<
    tGetConversationsFromChatDaddyData[]
  >([]);
  const [initialProgress, setInitialProgress] = useState<number>(0);
  const [handles, setHandles] = useState<string[]>([]);

  const { chatMessageBulkInsertFinishedEvent } = useSocketsContext();
  const { currentWorkspaceId } = useWorkspaceContext();

  const { addAlert } = useToastContext();

  const sendMetaBulkRequest = async () => {
    if (!currentWorkspaceId) return;
    await IntegrationsService.sendBulkMessagesToMeta({
      workspaceId: currentWorkspaceId,
      handles: handles,
    });
  };

  const sendWhatsappBulkRequest = async () => {
    if (!currentWorkspaceId) return;
    let bulkAccepted = true;
    await IntegrationsService.sendBulkMessages({
      workspace: currentWorkspaceId,
      handlesArray: handles,
    }).catch((error) => {
      // Preserve the existing 502/background-import compatibility behavior.
      // An HTTP acknowledgement does not prove that insertion has finished.
      if ((error as AxiosError).response?.status !== 502) {
        addAlert({
          type: eAlertType.ERROR,
          message:
            (error as Error).message ||
            'Ocurrió un error al sincronizar las conversaciones',
        });
        bulkAccepted = false;
      }
      LLog.error(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'SyncConversations.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error en sincronización de conversaciones',
        error as unknown as Error,
      );
    });
    // Keep the import offer available after transport failure. Staged history
    // remains readable until its retention window expires.
    if (!bulkAccepted) return;
    await IntegrationsService.updateAccountToNoMessagesHistory(
      currentWorkspaceId,
    ).catch((error: unknown) => {
      LLog.error(
        {
          component: LOG_COMPONENTS.SOCKET,
          method: 'SyncConversations.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al actualizar las opciones de sincronización',
        error as Error,
      );
    });
  };

  chatMessageBulkInsertFinishedEvent.useSubscription(() => {
    setSyncConversations((prev) => prev + 2);
  });

  useEffect(() => {
    // The provider outlives the modal. A fresh selection, including one with
    // the same number of chats, starts its own progress and completion count.
    setInitialProgress(0);
    setSyncConversations(0);
    if (conversations.length === 0 || handles.length === 0) return;
    if (selectedIntegration !== eIntegrationType.WHATSAPP_WEB) {
      sendMetaBulkRequest();
      setInitialProgress(100);
      return;
    }
    const totalDuration = conversations.length * 1000;
    const intervalDuration = totalDuration / 100;
    let progress = 0;
    const interval = setInterval(() => {
      progress += 1;
      setInitialProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        // Network effects must not run inside a React state updater, which
        // React can replay when it processes queued progress ticks.
        void sendWhatsappBulkRequest();
      }
    }, intervalDuration);

    return () => clearInterval(interval);
    // Changing channels alone must not replay the previous channel's handles.
    // The loader supplies a fresh selection before an import can start.
  }, [conversations, handles]);

  return {
    syncConversations,
    setSyncConversations,
    totalConversations,
    setTotalConversations,
    conversations,
    setConversations,
    initialProgress,
    setInitialProgress,
    handles,
    setHandles,
    selectedIntegration,
    setSelectedIntegration,
    selectedPagUUid,
    setSelectedPageUuid,
  };
}
